// reasoningUtils.js - Utilities for reasoning display in meal plan generation
import { useState } from 'react';

//------------------------------------------------
// CUSTOM HOOKS
//------------------------------------------------

/**
 * Custom hook to manage and display reasoning data during meal plan generation
 * @returns {Object} Reasoning state and control functions
 */
export const useReasoningDisplay = () => {
  const [reasoningSteps, setReasoningSteps] = useState([]);
  const [showReasoning, setShowReasoning] = useState(false);

  /**
   * Add a new reasoning step to the list
   * @param {String} step - The reasoning step to add
   */
  const addReasoningStep = (step) => {
    setReasoningSteps(prev => [...prev, step]);
  };

  /**
   * Clear all reasoning steps
   */
  const clearReasoningSteps = () => {
    setReasoningSteps([]);
  };

  /**
   * Toggle visibility of the reasoning panel
   */
  const toggleReasoningDisplay = () => {
    setShowReasoning(prev => !prev);
  };

  return {
    reasoningSteps,
    showReasoning,
    addReasoningStep,
    clearReasoningSteps,
    toggleReasoningDisplay
  };
};

//------------------------------------------------
// COMPONENTS
//------------------------------------------------

/**
 * Component to display the reasoning steps
 * 
 * @param {Array} reasoningSteps - List of reasoning steps to display
 * @param {Boolean} visible - Whether the panel is visible
 * @param {Function} onClose - Function to call when closing the panel
 * @returns {JSX.Element|null} The rendered component or null if not visible
 */
export const ReasoningPanel = ({ reasoningSteps, visible, onClose }) => {
  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg p-4 border-t border-gray-200 z-50 max-h-96 overflow-y-auto">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-lg font-semibold">AI Reasoning Process</h3>
        <button 
          onClick={onClose}
          className="text-gray-500 md:hover:text-gray-700"
        >
          ✕
        </button>
      </div>
      
      <div className="space-y-2">
        {reasoningSteps.length === 0 ? (
          <p className="text-gray-500 italic">No reasoning steps available yet.</p>
        ) : (
          reasoningSteps.map((step, index) => (
            <div key={index} className="p-2 bg-gray-50 rounded-md">
              <p className="text-sm whitespace-pre-wrap">{step}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

//------------------------------------------------
// UTILITY FUNCTIONS
//------------------------------------------------

/**
 * Parse and extract reasoning from streaming responses
 * 
 * @param {String} chunk - The chunk of streaming response to parse
 * @returns {String|null} The extracted reasoning text or null if not found
 */
export const extractReasoning = (chunk) => {
  try {
    if (typeof chunk !== 'string' || !chunk.startsWith('data:')) {
      return null;
    }
    
    const jsonData = JSON.parse(chunk.slice(5));
    if (jsonData.choices && jsonData.choices[0]?.delta?.reasoning) {
      return jsonData.choices[0].delta.reasoning;
    }
    
    return null;
  } catch (e) {
    console.warn('Error extracting reasoning:', e);
    return null;
  }
};